<script setup>
import { launchSupportBeacon } from "@/Composables/launchSupportBeacon.js";
import {
    Dialog,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Popover,
    PopoverButton,
    PopoverGroup,
    PopoverPanel,
} from "@headlessui/vue";
import {
    ChevronDownIcon,
    EnvelopeIcon,
    PlayCircleIcon,
} from "@heroicons/vue/20/solid";
import {
    Bars3Icon,
    BeakerIcon,
    ChatBubbleLeftRightIcon,
    CubeTransparentIcon,
    PresentationChartLineIcon,
    ShoppingBagIcon,
    ShoppingCartIcon,
    UserCircleIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline/index.js";
import { Head, Link, usePage } from "@inertiajs/vue3";
import { computed, onMounted, ref } from "vue";

import FooterNewsletterForm from "@/Pages/Info/Partials/FooterNewsletterForm.vue";

import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import ApplicationMark from "@/Components/ApplicationMark.vue";
import IconMcp from "@/Components/IconMcp.vue";

defineProps({
    canonicalLink: String,
    description: String,
    forceDark: {
        type: Boolean,
        default: false,
    },
    image: String,
    imageAlt: String,
    title: String,
});

const callsToAction = [
    {
        name: "Watch tutorial",
        href: "https://youtu.be/j6-hkbPSa1U",
        icon: PlayCircleIcon,
    },
    {
        name: "Contact sales",
        href: "mailto:sales@qualta.io",
        icon: EnvelopeIcon,
    },
];

const cases = [
    {
        name: "Know Your Customers",
        description: "Gain real customer intelligence.",
        route: "info.customer-intelligence",
        icon: ShoppingCartIcon,
    },
    {
        name: "Conduct Market Research",
        description: "Forecast changing tides in the market.",
        route: "info.market-research",
        icon: PresentationChartLineIcon,
    },
    {
        name: "Collect User Feedback",
        description: "Improve the usability of your SaaS.",
        route: "info.user-feedback",
        icon: UserCircleIcon,
    },
    {
        name: "Understand Your Employees",
        description: "Strengthen your company culture.",
        route: "info.employee-outreach",
        icon: CubeTransparentIcon,
    },
    {
        name: "Academic Research",
        description: "Conduct qualitative research at scale.",
        route: "info.academic-research",
        icon: BeakerIcon,
    },
];

const apps = [
    {
        name: "Shopify",
        description: "Talk to your Shopify customers.",
        route: "info.apps.shopify",
        icon: ShoppingBagIcon,
    },
    {
        name: "Chatbot",
        description: "Embed an AI interviewer on your site.",
        route: "info.apps.chatbot",
        icon: ChatBubbleLeftRightIcon,
    },
    {
        name: "MCP Server",
        description: "Connect Qualta to AI agents.",
        route: "info.apps.mcp-server",
        icon: IconMcp,
    },
];

const footerNavigation = computed(() => ({
    cases: [
        {
            name: "Customer Intelligence",
            href: route("info.customer-intelligence"),
        },
        {
            name: "Market Research",
            href: route("info.market-research"),
        },
        { name: "User Feedback", href: route("info.user-feedback") },
        {
            name: "Employee Outreach",
            href: route("info.employee-outreach"),
        },
        { name: "Academic Research", href: route("info.academic-research") },
    ],
    apps: [
        { name: "Shopify", href: route("info.apps.shopify") },
        { name: "Chatbot", href: route("info.apps.chatbot") },
        { name: "MCP Server", href: route("info.apps.mcp-server") },
    ],
    resources: [
        { name: "Pricing", href: route("info.pricing") },
        { name: "News", href: route("news.index") },
        { name: "API Documentation", href: "/docs/api" },
        { name: "Knowledge Base", href: "https://support.qualta.io" },
    ],
    legal: [
        { name: "Terms of service", href: route("terms.show") },
        { name: "Privacy policy", href: route("policy.show") },
    ],
    social: [
        {
            name: "LinkedIn",
            href: "https://www.linkedin.com/company/qualta-ai",
            icon: "fa-linkedin",
        },
        {
            name: "YouTube",
            href: "https://www.youtube.com/@QualtaAI",
            icon: "fa-square-youtube",
        },
        {
            name: "Blue Sky",
            href: "https://bsky.app/profile/qualtaai.bsky.social",
            icon: "fa-square-bluesky",
        },
        {
            name: "X-Twitter",
            href: "https://x.com/QualtaAI",
            icon: "fa-square-x-twitter",
        },
        {
            name: "Threads",
            href: "https://www.threads.com/@qualtaai",
            icon: "fa-square-threads",
        },
    ],
}));

const mobileMenuOpen = ref(false);

const page = usePage();

onMounted(() => {
    if (typeof window.Beacon === "function") {
        window.Beacon("logout");
        window.Beacon("destroy");
    }
});
</script>

<template>
    <Head :title="title">
        <link head-key="canonical" rel="canonical" :href="canonicalLink" />
        <meta head-key="og:title" property="og:title" :content="title" />
        <meta head-key="og:url" property="og:url" :content="canonicalLink" />
        <meta
            head-key="description"
            name="description"
            :content="description"
        />
        <meta
            head-key="og:description"
            property="og:description"
            :content="description"
        />
        <meta
            head-key="og:image"
            property="og:image"
            :content="
                image ??
                'https://qualta.io/assets/01KRKTC6QKHKAAFFWDCGYGRBK8.webp'
            "
        />
        <meta
            head-key="twitter:image:alt"
            name="twitter:image:alt"
            :content="imageAlt ?? 'Qualta AI software product screenshot'"
        />
    </Head>
    <div class="flex min-h-screen flex-col bg-white dark:bg-midnight">
        <!-- Header -->
        <header class="absolute inset-x-0 top-0 z-50">
            <nav
                class="flex items-center justify-between p-6 lg:px-8"
                aria-label="Global"
            >
                <div class="flex lg:flex-1 items-center gap-20">
                    <Link :href="route('home')" class="-m-1.5 p-1.5">
                        <span class="sr-only">Qualta</span>
                        <ApplicationLogo
                            :force-dark="forceDark"
                            class="h-8 w-auto"
                            aria-hidden="true"
                        />
                    </Link>
                    <PopoverGroup class="hidden lg:flex lg:gap-x-12">
                        <Popover class="relative">
                            <PopoverButton
                                :class="[
                                    forceDark
                                        ? 'text-gray-100'
                                        : 'text-gray-900 ',
                                    'shrink-0 whitespace-nowrap flex items-center gap-x-1 text-sm/6 font-semibold outline-hidden dark:text-gray-100 cursor-pointer',
                                ]"
                            >
                                Use Cases
                                <ChevronDownIcon
                                    class="size-5 flex-none text-gray-400"
                                    aria-hidden="true"
                                />
                            </PopoverButton>

                            <transition
                                enter-active-class="transition ease-out duration-200"
                                enter-from-class="opacity-0 translate-y-1"
                                enter-to-class="opacity-100 translate-y-0"
                                leave-active-class="transition ease-in duration-150"
                                leave-from-class="opacity-100 translate-y-0"
                                leave-to-class="opacity-0 translate-y-1"
                            >
                                <PopoverPanel
                                    class="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white dark:bg-gray-900 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-800/50"
                                >
                                    <div class="p-4 space-y-1">
                                        <div
                                            v-for="item in cases"
                                            :key="item.name"
                                            class="group relative flex gap-x-6 rounded-lg p-3 text-sm/6 hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <div
                                                class="mt-1 flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-700 group-hover:bg-white dark:group-hover:bg-gray-900"
                                            >
                                                <component
                                                    :is="item.icon"
                                                    class="size-6 text-gray-500 dark:text-gray-400 group-hover:text-brand-light dark:group-hover:text-brand-accent"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div class="flex-auto">
                                                <Link
                                                    :href="route(item.route)"
                                                    class="block font-semibold text-gray-900 dark:text-gray-100"
                                                    prefetch
                                                >
                                                    {{ item.name }}
                                                    <span
                                                        class="absolute inset-0"
                                                    />
                                                </Link>
                                                <p
                                                    class="mt-1 text-sm text-gray-600 dark:text-gray-300"
                                                >
                                                    {{ item.description }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        class="grid grid-cols-2 divide-x divide-gray-900/5 dark:divide-gray-600 bg-gray-50 dark:bg-gray-800"
                                    >
                                        <a
                                            v-for="item in callsToAction"
                                            :key="item.name"
                                            :href="item.href"
                                            target="_blank"
                                            class="flex items-center justify-center gap-x-2.5 p-3 text-sm/6 font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            <component
                                                :is="item.icon"
                                                class="size-5 flex-none text-gray-400 dark:text-gray-300"
                                                aria-hidden="true"
                                            />
                                            {{ item.name }}
                                        </a>
                                    </div>
                                </PopoverPanel>
                            </transition>
                        </Popover>

                        <Popover class="relative">
                            <PopoverButton
                                :class="[
                                    forceDark
                                        ? 'text-gray-100'
                                        : 'text-gray-900 ',
                                    'flex items-center gap-x-1 text-sm/6 font-semibold outline-hidden dark:text-gray-100 cursor-pointer',
                                ]"
                            >
                                Apps
                                <ChevronDownIcon
                                    class="size-5 flex-none text-gray-400"
                                    aria-hidden="true"
                                />
                            </PopoverButton>

                            <transition
                                enter-active-class="transition ease-out duration-200"
                                enter-from-class="opacity-0 translate-y-1"
                                enter-to-class="opacity-100 translate-y-0"
                                leave-active-class="transition ease-in duration-150"
                                leave-from-class="opacity-100 translate-y-0"
                                leave-to-class="opacity-0 translate-y-1"
                            >
                                <PopoverPanel
                                    class="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white dark:bg-gray-900 shadow-lg ring-1 ring-gray-900/5 dark:ring-gray-800/50"
                                >
                                    <div class="p-4 space-y-1">
                                        <div
                                            v-for="item in apps"
                                            :key="item.name"
                                            class="group relative flex gap-x-6 rounded-lg p-3 text-sm/6 hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <div
                                                class="mt-1 flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-700 group-hover:bg-white dark:group-hover:bg-gray-900"
                                            >
                                                <component
                                                    :is="item.icon"
                                                    class="size-6 text-gray-500 dark:text-gray-400 group-hover:text-brand-light dark:group-hover:text-brand-accent"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div class="flex-auto">
                                                <Link
                                                    :href="route(item.route)"
                                                    class="block font-semibold text-gray-900 dark:text-gray-100"
                                                    prefetch
                                                >
                                                    {{ item.name }}
                                                    <span
                                                        class="absolute inset-0"
                                                    />
                                                </Link>
                                                <p
                                                    class="mt-1 text-sm text-gray-600 dark:text-gray-300"
                                                >
                                                    {{ item.description }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </PopoverPanel>
                            </transition>
                        </Popover>

                        <Link
                            :href="route('news.index')"
                            :class="[
                                forceDark ? 'text-gray-100' : 'text-gray-900',
                                'text-sm/6 font-semibold dark:text-gray-100',
                            ]"
                            >News</Link
                        >
                        <Link
                            :href="route('info.pricing')"
                            :class="[
                                forceDark ? 'text-gray-100' : 'text-gray-900',
                                'text-sm/6 font-semibold dark:text-gray-100',
                            ]"
                            >Pricing</Link
                        >
                    </PopoverGroup>
                </div>
                <div class="flex lg:hidden">
                    <button
                        type="button"
                        :class="[
                            forceDark ? 'text-gray-400' : 'text-gray-700',
                            '-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 dark:text-gray-400',
                        ]"
                        @click="mobileMenuOpen = true"
                    >
                        <span class="sr-only">Open main menu</span>
                        <Bars3Icon class="size-6" aria-hidden="true" />
                    </button>
                </div>
                <div
                    class="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-6 lg:items-center"
                >
                    <Link
                        v-if="$page.props.auth.user"
                        :href="route('guides.index')"
                        class="text-sm/6 font-semibold text-white"
                        >Go to Guides
                        <span aria-hidden="true">&rarr;</span></Link
                    >
                    <Link
                        v-if="!$page.props.auth.user"
                        :href="route('login')"
                        class="text-sm/6 font-semibold text-white"
                        prefetch
                        >Log in <span aria-hidden="true">&rarr;</span></Link
                    >
                    <Link
                        v-if="!$page.props.auth.user"
                        :href="route('register')"
                        class="py-1 px-2 text-sm/6 font-semibold text-white border border-white rounded-xs"
                        prefetch
                        >Start for FREE</Link
                    >
                </div>
            </nav>
            <Dialog
                class="lg:hidden"
                @close="mobileMenuOpen = false"
                :open="mobileMenuOpen"
            >
                <div class="fixed inset-0 z-50" />
                <DialogPanel
                    class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-gray-900 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-white/10"
                >
                    <div class="flex items-center justify-between">
                        <Link :href="route('home')" class="-m-1.5">
                            <span class="sr-only">Qualta</span>
                            <ApplicationMark
                                :force-dark="true"
                                class="h-10 w-auto"
                                aria-hidden="true"
                            />
                        </Link>
                        <button
                            type="button"
                            class="-m-2.5 rounded-md p-2.5 text-gray-400"
                            @click="mobileMenuOpen = false"
                        >
                            <span class="sr-only">Close menu</span>
                            <XMarkIcon class="size-6" aria-hidden="true" />
                        </button>
                    </div>
                    <div class="mt-6 flow-root">
                        <div class="-my-6 divide-y divide-gray-500/25">
                            <div class="space-y-1 py-6">
                                <Disclosure
                                    as="div"
                                    class="-mx-3"
                                    v-slot="{ open }"
                                >
                                    <DisclosureButton
                                        class="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800 cursor-pointer"
                                    >
                                        Use Cases
                                        <ChevronDownIcon
                                            :class="[
                                                open ? 'rotate-180' : '',
                                                'size-5 flex-none',
                                            ]"
                                            aria-hidden="true"
                                        />
                                    </DisclosureButton>
                                    <DisclosurePanel class="mt-2 space-y-1">
                                        <Link
                                            v-for="item in cases"
                                            :key="item.name"
                                            :href="route(item.route)"
                                            class="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-white hover:bg-gray-800"
                                            prefetch
                                            >{{ item.name }}</Link
                                        >
                                    </DisclosurePanel>
                                </Disclosure>
                                <Disclosure
                                    as="div"
                                    class="-mx-3"
                                    v-slot="{ open }"
                                >
                                    <DisclosureButton
                                        class="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800 cursor-pointer"
                                    >
                                        Apps
                                        <ChevronDownIcon
                                            :class="[
                                                open ? 'rotate-180' : '',
                                                'size-5 flex-none',
                                            ]"
                                            aria-hidden="true"
                                        />
                                    </DisclosureButton>
                                    <DisclosurePanel class="mt-2 space-y-1">
                                        <Link
                                            v-for="item in apps"
                                            :key="item.name"
                                            :href="route(item.route)"
                                            class="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-white hover:bg-gray-800"
                                            prefetch
                                            >{{ item.name }}</Link
                                        >
                                    </DisclosurePanel>
                                </Disclosure>
                                <Link
                                    :href="route('news.index')"
                                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800"
                                    prefetch
                                    >News</Link
                                >
                                <Link
                                    :href="route('info.pricing')"
                                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800"
                                    prefetch
                                    >Pricing</Link
                                >
                            </div>
                            <div class="space-y-1 py-6">
                                <Link
                                    v-if="$page.props.auth.user"
                                    :href="route('guides.index')"
                                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800"
                                    >Go to Guides</Link
                                >
                                <Link
                                    v-if="!$page.props.auth.user"
                                    :href="route('login')"
                                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800"
                                    prefetch
                                    >Log in</Link
                                >
                                <Link
                                    v-if="!$page.props.auth.user"
                                    :href="route('register')"
                                    class="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-white hover:bg-gray-800"
                                    prefetch
                                    >Start for FREE</Link
                                >
                            </div>
                        </div>
                    </div>
                </DialogPanel>
            </Dialog>
        </header>
        <main class="flex-1">
            <slot></slot>
        </main>
        <!-- Footer -->
        <footer class="mt-32 bg-gray-900 sm:mt-56">
            <div
                class="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32"
            >
                <div class="xl:grid xl:grid-cols-3 xl:gap-8">
                    <Link :href="route('home')"
                        ><ApplicationLogo :force-dark="true" class="h-8"
                    /></Link>

                    <div
                        class="mt-16 grid grid-cols-2 max-sm:gap-8 xl:col-span-2 xl:mt-0"
                    >
                        <div class="md:grid md:grid-cols-2 md:gap-8">
                            <div>
                                <h3 class="text-sm/6 font-semibold text-white">
                                    Use Cases
                                </h3>
                                <ul
                                    role="list"
                                    class="mt-6 space-y-4 md:space-y-3"
                                >
                                    <li
                                        v-for="item in footerNavigation.cases"
                                        :key="item.name"
                                    >
                                        <Link
                                            :href="item.href"
                                            class="text-sm/6 text-gray-400 hover:text-white"
                                            prefetch
                                            >{{ item.name }}</Link
                                        >
                                    </li>
                                </ul>
                            </div>
                            <div class="mt-10 md:mt-0">
                                <h3 class="text-sm/6 font-semibold text-white">
                                    Apps
                                </h3>
                                <ul
                                    role="list"
                                    class="mt-6 space-y-4 md:space-y-3"
                                >
                                    <li
                                        v-for="item in footerNavigation.apps"
                                        :key="item.name"
                                    >
                                        <Link
                                            :href="item.href"
                                            class="text-sm/6 text-gray-400 hover:text-white"
                                            prefetch
                                            >{{ item.name }}</Link
                                        >
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="md:grid md:grid-cols-2 md:gap-8">
                            <div>
                                <h3 class="text-sm/6 font-semibold text-white">
                                    Resources
                                </h3>
                                <ul
                                    role="list"
                                    class="mt-6 space-y-4 md:space-y-3"
                                >
                                    <li
                                        v-for="item in footerNavigation.resources"
                                        :key="item.name"
                                    >
                                        <a
                                            :href="item.href"
                                            class="text-sm/6 text-gray-400 hover:text-white"
                                            >{{ item.name }}</a
                                        >
                                    </li>
                                    <li>
                                        <button
                                            @click="launchSupportBeacon(page)"
                                            class="text-sm/6 text-gray-400 hover:text-white cursor-pointer"
                                        >
                                            Support
                                        </button>
                                    </li>
                                </ul>
                            </div>
                            <div class="mt-10 md:mt-0">
                                <h3 class="text-sm/6 font-semibold text-white">
                                    Legal
                                </h3>
                                <ul
                                    role="list"
                                    class="mt-6 space-y-4 md:space-y-3"
                                >
                                    <li
                                        v-for="item in footerNavigation.legal"
                                        :key="item.name"
                                    >
                                        <Link
                                            :href="item.href"
                                            class="text-sm/6 text-gray-400 hover:text-white"
                                            prefetch
                                            >{{ item.name }}</Link
                                        >
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <FooterNewsletterForm />
                <div
                    class="mt-8 border-t border-white/10 pt-8 md:flex md:items-center md:justify-between"
                >
                    <div class="flex gap-x-6 md:gap-x-4 md:order-2">
                        <a
                            v-for="item in footerNavigation.social"
                            :key="item.name"
                            :href="item.href"
                            target="_blank"
                            class="text-gray-400 hover:text-gray-300"
                        >
                            <span class="sr-only">{{ item.name }}</span>
                            <faIcon
                                :icon="['fab', item.icon]"
                                class="size-6!"
                                aria-hidden="true"
                            />
                        </a>
                    </div>
                    <p class="mt-8 text-sm/6 text-gray-400 md:order-1 md:mt-0">
                        Copyright &copy; Qualta, LLC
                        {{ new Date().getFullYear() }}. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    </div>
</template>

<style scoped></style>
